import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import { ConfirmationWindowComponent } from '@components/confirmation-window/confirmation-window.component';
import { LeavePageConfirmationComponent } from '@components/leave-page-confirmation/leave-page-confirmation.component';
import { ChannelService } from '@service/channel.service';
import { TrendGraphViewsService } from '@service/trend-graph-views.service';
import { Subscription, Observable, lastValueFrom, take } from 'rxjs';
import { TrendGraphCreateComponent } from './trend-graph-create/trend-graph-create.component';
import { TrendGraphView } from 'ops/common/models/trend-graph-view';
import { ResponsiveService } from '@service/responsive.service';
import { TrendGraphAccessSettingsService } from '@service/trend-graph-access-settings.service';
import { AccessType, UserAccess } from 'ops/common/models/access-settings';
import { AuthService } from '@service/auth.service';
import _ from 'lodash';
import { Channel } from 'ops/common/models/channel';
import { TrendGraphSocketService } from '@service/trend-graph-view-socket.service';

export const TREND_GRAPH_BASE_ROUTE = '/ops/trendgraphs';

@Component({
    selector: 'app-ops-trend-graph',
    templateUrl: './ops-trend-graph.component.html',
    styleUrls: ['./ops-trend-graph.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OpsTrendGraphComponent implements OnInit, OnDestroy {
    DEFAULT_TITLE: string = 'Custom Trend Graph Name';

    VIEWMODE_VIEW = 'view';
    VIEWMODE_CREATE = 'create';
    VIEWMODE_EDIT = 'edit';

    @ViewChild('topElementSmallView') topElementSmallView;

    @ViewChild('createForm') createForm: TrendGraphCreateComponent;
    @ViewChild('createFormConfirmOverlay') leavePageOverlay: LeavePageConfirmationComponent;
    @ViewChild('deleteConfirmationOverlay') deleteConfirmOverlay: ConfirmationWindowComponent;

    @ViewChild('scrollContainer') scrollContainer: ElementRef;

    views: TrendGraphView[];
    filteredTrendGraphs: TrendGraphView[];
    cloneViews: TrendGraphView[];
    selectedView: TrendGraphView;
    selectedViewUsersAccess: UserAccess[];
    editView: TrendGraphView;
    createView: TrendGraphView;

    viewMode: string = this.VIEWMODE_VIEW;

    isChannelsInit: boolean = false;
    isViewsInit: boolean = false;

    isInitComplete: boolean = false;
    targetViewIndex: number = 0;
    projectViewsLength: number;

    viewParameter: number;

    channels: Channel[] = [];
    channelLookup: { [fcuChId: number]: Channel } = {};

    urlSubscription: Subscription;

    isFormSubmitted = false;
    inputTrendGraph: string;

    currentUserSubjectSubscription: Subscription;
    accessUpdatedSubscription: Subscription;

    constructor(
        private viewService: TrendGraphViewsService,
        private route: ActivatedRoute,
        private router: Router,
        private channelService: ChannelService,
        public responsive: ResponsiveService,
        private trendGraphAccessService: TrendGraphAccessSettingsService,
        private authService: AuthService,
        private cdr: ChangeDetectorRef,
        private trendGraphSocketService: TrendGraphSocketService,
    ) {
        const viewFromRoute = this.router.getCurrentNavigation()?.extras.state?.view;
        if (viewFromRoute) {
            this.createView = viewFromRoute;
        }

        this.urlSubscription = this.route.params.subscribe((params) => {
            const viewParameter = params['viewOption'];
            const editParameter = params['editOption'];

            this.viewParameter = Number(viewParameter) || 0;

            const noGraphsExist = this.isInitComplete && this.filteredTrendGraphs?.length == 0;

            if (editParameter !== this.VIEWMODE_EDIT) this.editView = null;
            this.isFormSubmitted = false;

            if (viewParameter == this.VIEWMODE_CREATE || noGraphsExist) {
                this.viewMode = this.VIEWMODE_CREATE;
            } else {
                if (editParameter == this.VIEWMODE_EDIT) {
                    this.viewMode = this.VIEWMODE_EDIT;
                } else {
                    this.viewMode = this.VIEWMODE_VIEW;
                    this.filteredTrendGraphs = _.cloneDeep(this.cloneViews);
                }

                let targetView = parseInt(viewParameter) || 0;

                if (this.isViewsInit) {
                    targetView = Math.min(targetView, this.filteredTrendGraphs.length - 1);
                    this.setView(this.filteredTrendGraphs[targetView]);
                    this.setEditViewToCurrent();
                }
                this.targetViewIndex = targetView;
                this.setViewUsersAccess();
            }
            this.cdr.markForCheck();
        });
    }

    ngOnInit(): void {
        this.loadViews();
        this.loadChannels();
        this.trendGraphSocketService.startSubscriptions();
        this.trendGraphSocketService.updateViewsSubject().subscribe(() => {
            this.loadViews();
        });
        this.currentUserSubjectSubscription = this.authService.currentUser$.subscribe(() => {
            if (this.deleteConfirmOverlay?.style.display != 'none')
                this.deleteConfirmOverlay.emitValue(false);
            if (this.viewMode !== this.VIEWMODE_CREATE) {
                this.viewMode = this.VIEWMODE_VIEW;
                this.loadViews();
            }
            this.cdr.markForCheck();
        });

        this.route.params.subscribe((params) => {
            if (params.viewOption == this.VIEWMODE_CREATE) {
                this.viewMode = this.VIEWMODE_CREATE;
                this.cdr.markForCheck();
            }
        });
    }

    ngOnDestroy(): void {
        this.trendGraphSocketService.destroySubscriptions();
        this.urlSubscription.unsubscribe();
        this.currentUserSubjectSubscription?.unsubscribe();
        this.accessUpdatedSubscription?.unsubscribe();
    }

    loadChannels() {
        this.channelService.getChannels().subscribe((channels: Channel[]) => {
            this.channels = channels;

            for (let i = 0; i < channels.length; i++) {
                this.channelLookup[channels[i].fcuData.fcuChannelId] = channels[i];
            }

            this.isChannelsInit = true;
            this.evaluateInitComplete();
        });
    }

    loadViews() {
        this.viewService.getAll().subscribe((views) => {
            this.projectViewsLength = views.length;
            this.views = views;
            this.filteredTrendGraphs = [];
            const currentUserId = this.authService.getCurrentUserId();
            if (!currentUserId) {
                views.map((view) => {
                    if (view.publicAccess) {
                        this.filteredTrendGraphs.push(view);
                    }
                });
            } else {
                views.map((view) => {
                    if (view.publicAccess) {
                        this.filteredTrendGraphs.push(view);
                    } else if ((view as any).AccessTrendGraphs.length > 0) {
                        (view as any).AccessTrendGraphs.map((usersAccess) => {
                            if (
                                usersAccess.roleId === currentUserId &&
                                usersAccess.accessLevel !== AccessType.NoAccess
                            ) {
                                this.filteredTrendGraphs.push(view);
                            }
                        });
                    }
                });
            }

            this.cloneViews = _.cloneDeep(this.filteredTrendGraphs);

            const viewIndex = Math.min(this.targetViewIndex, this.filteredTrendGraphs.length - 1);

            if (this.filteredTrendGraphs.length == 0) {
                this.openCreatePage();
            } else {
                if (!this.filteredTrendGraphs[this.viewParameter]) {
                    this.targetViewIndex = viewIndex;
                } else {
                    this.targetViewIndex = this.viewParameter;
                }
                this.setView(this.filteredTrendGraphs[this.targetViewIndex]);

                if (this.viewMode == this.VIEWMODE_EDIT) this.setEditViewToCurrent();
            }

            this.isViewsInit = true;
            this.evaluateInitComplete();
            this.setViewUsersAccess();
            this.cdr.markForCheck();
        });
    }

    resetViewInitStatus() {
        this.isViewsInit = false;
        this.evaluateInitComplete();
    }

    setEditViewToCurrent() {
        this.editView = JSON.parse(JSON.stringify(this.selectedView));
    }

    editCurrentView() {
        const viewIndex = this.filteredTrendGraphs.indexOf(this.selectedView);

        if (viewIndex == -1) {
            console.error('Error, trying to edit a view that does not exist');
        }

        this.router.navigate(['/ops/trendgraphs', viewIndex, 'edit']);
    }

    deleteCurrentView() {
        const viewIndex = this.filteredTrendGraphs.indexOf(this.selectedView);

        if (viewIndex == -1) {
            console.error('Error, trying to delete a view that does not exist');
        }

        this.deleteConfirmOverlay.setVisible(true);
        const onDeleteConfirm = this.deleteConfirmOverlay.onOptionSelected.subscribe(
            async (v: boolean) => {
                onDeleteConfirm.unsubscribe();
                if (v) {
                    await this.viewService.delete(this.selectedView.id);
                    this.loadViews();
                    if (this.targetViewIndex === 0 && this.filteredTrendGraphs.length === 1) {
                        this.openCreatePage();
                    } else if (
                        this.targetViewIndex === 0 &&
                        this.filteredTrendGraphs.length !== 1
                    ) {
                        this.openView(this.targetViewIndex);
                    } else {
                        this.openView(this.targetViewIndex - 1);
                    }
                }
                this.cdr.markForCheck();
            },
        );
    }

    openCreatePage() {
        this.createView = {
            name: `${this.DEFAULT_TITLE} ${this.projectViewsLength + 1}`,
            description: '',
            channels: [],
            publicAccess: true,
        };
        this.router.navigate(['/ops/trendgraphs', 'create']);
    }

    openView(viewIndex: number) {
        this.router.navigate(['/ops/trendgraphs', viewIndex]);
    }

    private evaluateInitComplete() {
        this.isInitComplete = this.isViewsInit && this.isChannelsInit;
        if (this.isInitComplete) {
            this.cdr.markForCheck();
        }
    }

    private setView(view: TrendGraphView) {
        this.selectedView = view;
    }

    static CanDeactivate(
        component: OpsTrendGraphComponent,
        currentRoute: ActivatedRouteSnapshot,
        currentState: RouterStateSnapshot,
        nextState: RouterStateSnapshot,
    ): boolean | Observable<boolean> {
        const canExit =
            component.isFormSubmitted ||
            (component.viewMode !== component.VIEWMODE_CREATE &&
                component.viewMode !== component.VIEWMODE_EDIT) ||
            component.tryCloseCreateForm();
        return canExit;
    }

    tryCloseCreateForm(): boolean | Observable<boolean> {
        if (this.createForm && !this.createForm.hasUnsavedChanges()) {
            return true;
        } else {
            this.leavePageOverlay.setVisible(true);
            this.cdr.markForCheck();
            return this.leavePageOverlay.onOptionSelected;
        }
    }

    isSmallScreenDesign() {
        return (
            (this.responsive.isSmall() ||
                this.responsive.isXSmall() ||
                this.responsive.isHandset() ||
                this.responsive.isTabletPortrait() ||
                this.responsive.isWebPortrait()) &&
            !this.responsive.isMedium()
        );
    }

    async handleCancel(displayOverlay) {
        let leavePage = true;
        if (displayOverlay) {
            this.leavePageOverlay.setVisible(true);
            leavePage = await lastValueFrom(this.leavePageOverlay.onOptionSelected.pipe(take(1)));
        }
        if (leavePage) {
            const currentViewIndex = this.filteredTrendGraphs.indexOf(this.selectedView);
            this.filteredTrendGraphs[currentViewIndex] = _.cloneDeep(
                this.cloneViews[currentViewIndex],
            );
            if (currentViewIndex != -1) {
                this.router.navigate(['/ops/trendgraphs', currentViewIndex]);
                this.viewMode = this.VIEWMODE_VIEW;
            } else {
                console.error('Updated view not in views list');
            }
        }
        return;
    }

    shouldShowScrollTopButton() {
        if (this.topElementSmallView) {
            const rect = this.topElementSmallView.nativeElement.getBoundingClientRect();
            const topShown = rect.top >= 0;

            if (!topShown) {
                return (
                    this.responsive.isXSmall() ||
                    this.responsive.isHandset() ||
                    this.responsive.isTabletPortrait()
                );
            }
        }
        return false;
    }

    scrollToTop() {
        this.topElementSmallView.nativeElement.scrollIntoView({ behavior: 'smooth' });
    }

    handleCreateFormSubmit(submittedView) {
        this.targetViewIndex = this.filteredTrendGraphs.length;
        this.isFormSubmitted = true;
        if (this.viewMode == this.VIEWMODE_EDIT) {
            const currentViewIndex = this.filteredTrendGraphs.indexOf(this.selectedView);

            if (currentViewIndex != -1) {
                this.filteredTrendGraphs[currentViewIndex] = submittedView;
                this.targetViewIndex = currentViewIndex;
                this.targetViewIndex = currentViewIndex;
            } else {
                console.error('Updated view not in views list');
            }
        } else {
            this.resetViewInitStatus();
            this.loadViews();
        }

        this.createView = null;
        this.openView(this.targetViewIndex);
        this.cdr.markForCheck();
    }

    async setViewUsersAccess() {
        if (this.selectedView) {
            this.selectedViewUsersAccess = await this.trendGraphAccessService.getAll(
                this.selectedView.id,
            );
        } else if (this.filteredTrendGraphs?.length > 0) {
            const activeView = this.filteredTrendGraphs[this.targetViewIndex];
            this.selectedViewUsersAccess = await this.trendGraphAccessService.getAll(activeView.id);
        }
    }
}
